import React, { useRef, useEffect, useState, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { MemberProvider, useMember } from './lib/memberAuth';
import { authAPI, publicAPI } from './lib/api';
import { Toaster } from 'sonner';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoginModal from './components/LoginModal';
import HomePage from './pages/HomePage';
import { getRootPersonality, getPersonalityVisibility } from './lib/pbPersonality';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ReadingListPage from './pages/ReadingListPage';
import GalleryPage from './pages/GalleryPage';
import MapDetailPage from './pages/MapDetailPage';
import CheckoutSuccess from './pages/CheckoutSuccess';
import FeaturedProjectsPage from './pages/FeaturedProjectsPage';
import ConferencesPage, { RecommendedSitesPage } from './pages/MapTypePage';
import DynamicPage from './pages/DynamicPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import LayoutSubGallery from './components/layouts/LayoutSubGallery';
// Eager (public-facing) pages — kept in the main bundle.
import LandingPage from './pages/LandingPage';
import MembershipEnrollment from './pages/MembershipEnrollment';
import { CmsSectionGuard } from './pages/admin/Forbidden';

// Admin + Member portal pages are lazy-loaded so the PUBLIC bundle no longer
// ships the entire CMS (recharts, react-quill, @dnd-kit, admin leaflet, …).
// Each becomes its own async chunk, fetched only when its route is visited.
// All static `import`s must stay above these `const` declarations (import/first).
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const HeroManager = lazy(() => import('./pages/admin/HeroManager'));
const HeroSlideForm = lazy(() => import('./pages/admin/HeroSlideForm'));
const HeroAbAnalytics = lazy(() => import('./pages/admin/HeroAbAnalytics'));
const AboutManager = lazy(() => import('./pages/admin/AboutManager'));
const ServicesManager = lazy(() => import('./pages/admin/ServicesManager'));
const BlogManager = lazy(() => import('./pages/admin/BlogManager'));
const BooksManager = lazy(() => import('./pages/admin/BooksManager'));
const MapsManager = lazy(() => import('./pages/admin/MapsManager'));
const GalleryManager = lazy(() => import('./pages/admin/GalleryManager'));
const GalleryAlbumsManager = lazy(() => import('./pages/admin/GalleryAlbumsManager'));
const PortfolioManager = lazy(() => import('./pages/admin/PortfolioManager'));
const TestimonialsManager = lazy(() => import('./pages/admin/TestimonialsManager'));
const ContactsManager = lazy(() => import('./pages/admin/ContactsManager'));
const PurchasesManager = lazy(() => import('./pages/admin/PurchasesManager'));
const SettingsManager = lazy(() => import('./pages/admin/SettingsManager'));
const EmailManagement = lazy(() => import('./pages/admin/EmailManagement'));
const MailManager = lazy(() => import('./pages/admin/MailManager'));
const ProsManager = lazy(() => import('./pages/admin/ProsManager'));
const PagesManager = lazy(() => import('./pages/admin/PagesManager'));
const UsersManager = lazy(() => import('./pages/admin/UsersManager'));
const AnalyticsDashboard = lazy(() => import('./pages/admin/AnalyticsDashboard'));
const SeoManager = lazy(() => import('./pages/admin/SeoManager'));
const SectionOrderManager = lazy(() => import('./pages/admin/SectionOrderManager'));
const MembersManager = lazy(() => import('./pages/admin/MembersManager'));
const MemberSignatures = lazy(() => import('./pages/admin/MemberSignatures'));
const MemberPoints = lazy(() => import('./pages/admin/MemberPoints'));
const RewardsManager = lazy(() => import('./pages/admin/RewardsManager'));
const MemberLogins = lazy(() => import('./pages/admin/MemberLogins'));
const MemberLevelsManager = lazy(() => import('./pages/admin/MemberLevelsManager'));
const MemberTypesManager = lazy(() => import('./pages/admin/MemberTypesManager'));
const MembershipSettingsManager = lazy(() => import('./pages/admin/MembershipSettingsManager'));
const BackupManager = lazy(() => import('./pages/admin/BackupManager'));
const ContactSettingsManager = lazy(() => import('./pages/admin/ContactSettingsManager'));
const LandingContentManager = lazy(() => import('./pages/admin/LandingContentManager'));
const LandingSubscribersManager = lazy(() => import('./pages/admin/LandingSubscribersManager'));
const LandingContactsManager = lazy(() => import('./pages/admin/LandingContactsManager'));
const LandingHeroManager = lazy(() => import('./pages/admin/LandingHeroManager'));
const LandingHeroSlideForm = lazy(() => import('./pages/admin/LandingHeroSlideForm'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
// Membership / My Account
const MemberLogin = lazy(() => import('./pages/myaccount/MemberLogin'));
const MemberRegister = lazy(() => import('./pages/myaccount/MemberRegister'));
const MemberForgotPassword = lazy(() => import('./pages/myaccount/MemberForgotPassword'));
const MemberResetPassword = lazy(() => import('./pages/myaccount/MemberResetPassword'));
const MyAccountLayout = lazy(() => import('./pages/myaccount/MyAccountLayout'));
const MembershipProfile = lazy(() => import('./pages/myaccount/MembershipProfile'));
const MentorshipProfile = lazy(() => import('./pages/myaccount/MentorshipProfile'));
const MySponsor = lazy(() => import('./pages/myaccount/MySponsor'));
const InviteCode = lazy(() => import('./pages/myaccount/InviteCode'));
const MyCommunity = lazy(() => import('./pages/myaccount/MyCommunity'));
const MyEbank = lazy(() => import('./pages/myaccount/MyEbank'));
const PortfolioList = lazy(() => import('./pages/myaccount/PortfolioList'));
const PortfolioDetail = lazy(() => import('./pages/myaccount/PortfolioDetail'));
const PortfolioForm = lazy(() => import('./pages/myaccount/PortfolioForm'));
const GlobalCalendar = lazy(() => import('./pages/myaccount/GlobalCalendar'));
const MentorshipCalendar = lazy(() => import('./pages/myaccount/MentorshipCalendar'));
const MentorCalendarView = lazy(() => import('./pages/myaccount/MentorCalendarView'));
const MyBookings = lazy(() => import('./pages/myaccount/MyBookings'));
const CalendarSync = lazy(() => import('./pages/myaccount/CalendarSync'));
const MentorshipCheckoutSuccess = lazy(() => import('./pages/myaccount/MentorshipCheckoutSuccess'));
const MentorEarnings = lazy(() => import('./pages/myaccount/MentorEarnings'));
const PointsRewards = lazy(() => import('./pages/myaccount/PointsRewards'));
const BundlesBrowse = lazy(() => import('./pages/myaccount/BundlesBrowse'));
const BundleDetail = lazy(() => import('./pages/myaccount/BundleDetail'));
const MemberMail = lazy(() => import('./pages/myaccount/Mail'));
const BundleCheckoutSuccess = lazy(() => import('./pages/myaccount/BundleCheckoutSuccess'));
const AdminBundlesManager = lazy(() => import('./pages/admin/AdminBundlesManager'));
const AdminPayoutsManager = lazy(() => import('./pages/admin/AdminPayoutsManager'));
const AdminCouponsManager = lazy(() => import('./pages/admin/AdminCouponsManager'));
const AurexSectionsManager = lazy(() => import('./pages/admin/AurexSectionsManager'));
const EventDetail = lazy(() => import('./pages/myaccount/EventDetail'));
const EnrollmentFieldsManager = lazy(() => import('./pages/admin/EnrollmentFieldsManager'));
const GeoManager = lazy(() => import('./pages/admin/GeoManager'));
const DocumentationManager = lazy(() => import('./pages/admin/DocumentationManager'));
const QuickLinksManager = lazy(() => import('./pages/admin/QuickLinksManager'));
const MyAccountNavManager = lazy(() => import('./pages/admin/MyAccountNavManager'));
const GlobalEventsManager = lazy(() => import('./pages/admin/GlobalEventsManager'));
const MentorshipScheduleManager = lazy(() => import('./pages/admin/MentorshipScheduleManager'));
const MentorSlotTemplatesManager = lazy(() => import('./pages/admin/MentorSlotTemplatesManager'));
const BlockedDatesManager = lazy(() => import('./pages/admin/BlockedDatesManager'));
const RolesManager = lazy(() => import('./pages/admin/RolesManager'));
const CmsWelcome = lazy(() => import('./pages/admin/CmsWelcome'));

import { injectThemeColors } from './lib/themeColors';
import { LanguageProvider } from './lib/i18n';
import { t as i18nT } from './lib/i18n';
import BackToTop from './components/BackToTop';

// Global settings context for colors and theme
export const SettingsContext = createContext({});
export const useSettings = () => useContext(SettingsContext);

export const ThemeContext = createContext('default');
export const useTheme = () => useContext(ThemeContext);

function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({ _loaded: false });
  useEffect(() => {
    publicAPI.getSettings()
      .then(r => setSettings({ ...(r.data || {}), _loaded: true }))
      .catch(() => setSettings({ _loaded: true }));
  }, []);

  // Apply CSS variables from theme_colors (new) and colors (legacy).
  // active_theme is included so the Personal Brand Pro palette override (which
  // runs at the end of injectThemeColors) fires whenever the theme changes.
  useEffect(() => {
    const themeColors = settings.theme_colors || {};
    // Migrate legacy colors.* into website group if theme_colors.website is empty
    if (!themeColors.website && settings.colors) {
      themeColors.website = settings.colors;
    }
    injectThemeColors(themeColors, {
      my_account_color_scheme: settings.my_account_color_scheme,
      active_theme: settings.active_theme,
    });
  }, [settings.theme_colors, settings.colors, settings.my_account_color_scheme, settings.active_theme]);

  // Dynamic favicon
  useEffect(() => {
    const faviconUrl = settings.favicon;
    if (!faviconUrl) return;
    const src = faviconUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${faviconUrl}` : faviconUrl;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = src;
  }, [settings.favicon]);

  // Dynamic page title from tagline — pick the admin's default language
  // (multi-language tab update is not critical here; fresh load always gets
  // the right one).
  useEffect(() => {
    const lang = localStorage.getItem('aurex_locale') || settings.default_language || 'en';
    const tagline = i18nT(settings.tagline, lang);
    const brand = i18nT(settings.brand_name, lang);
    if (tagline) {
      document.title = tagline;
    } else if (brand) {
      document.title = brand;
    }
  }, [settings.tagline, settings.brand_name, settings.default_language]);

  const activeTheme = settings.active_theme || 'default';

  return (
    <SettingsContext.Provider value={settings}>
      <ThemeContext.Provider value={activeTheme}>
        <LanguageProvider settings={settings}>
          {children}
        </LanguageProvider>
      </ThemeContext.Provider>
    </SettingsContext.Provider>
  );
}

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
    if (!sessionId) { navigate('/'); return; }
    (async () => {
      try {
        const res = await authAPI.exchangeSession(sessionId);
        setUserData(res.data);
        navigate('/', { replace: true });
      } catch { navigate('/', { replace: true }); }
    })();
  }, [navigate, setUserData]);
  return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full"></div></div>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full"></div></div>;
  // Admins pass immediately. Operators need at least one CMS permission.
  if (!user) return <Navigate to="/admin/login" replace />;
  const hasAnyCmsAccess = user.role === 'admin' || ((user.effective_permissions || []).length > 0);
  if (!hasAnyCmsAccess) return <Navigate to="/admin/login" replace />;
  return children;
}

/* Single source of truth for every /admin sub-route.
 *   `section` maps the route to a CMS Section Registry key; CmsSectionGuard
 *   renders a 403 panel (inside AdminLayout) for operators without the perm.
 *   `adminOnly` locks a route to accounts with `role: "admin"` regardless of
 *   their granted CMS permissions (currently only Roles & Permissions and the
 *   legacy Users manager). */
/* Admin index route picker.
 *   Admin and operators with the `dashboard` permission see the full dashboard;
 *   operators without it land on the CMS Welcome page (greeting + admin-managed
 *   rich-text from Settings → General → CMS Welcome). This guarantees every
 *   logged-in CMS user lands on a meaningful screen, not on a 403. */
function AdminIndexRouter() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const perms = user?.effective_permissions || [];
  const canSeeDashboard = isAdmin || perms.includes('dashboard');
  return canSeeDashboard ? <AdminDashboard /> : <CmsWelcome />;
}

const ADMIN_ROUTES = [
  { index: true, el: <AdminIndexRouter /> },
  { path: 'hero',                            section: 'hero',                             el: <HeroManager /> },
  { path: 'hero/add',                        section: 'hero',                             el: <HeroSlideForm /> },
  { path: 'hero/edit/:id',                   section: 'hero',                             el: <HeroSlideForm /> },
  { path: 'hero-ab',                         section: 'hero_ab',                          el: <HeroAbAnalytics /> },
  { path: 'about',                           section: 'about',                            el: <AboutManager /> },
  { path: 'services',                        section: 'services',                         el: <ServicesManager /> },
  { path: 'blog',                            section: 'blog',                             el: <BlogManager /> },
  { path: 'books',                           section: 'books',                            el: <BooksManager /> },
  { path: 'maps',                            section: 'maps',                             el: <MapsManager /> },
  { path: 'gallery',                         section: 'gallery',                          el: <GalleryManager /> },
  { path: 'gallery-albums',                  section: 'gallery_albums',                   el: <GalleryAlbumsManager /> },
  { path: 'portfolio',                       section: 'portfolio',                        el: <PortfolioManager /> },
  { path: 'testimonials',                    section: 'testimonials',                     el: <TestimonialsManager /> },
  { path: 'contacts',                        section: 'contacts',                         el: <ContactsManager /> },
  { path: 'contact-settings',                section: 'contact_settings',                 el: <ContactSettingsManager /> },
  { path: 'purchases',                       section: 'purchases',                        el: <PurchasesManager /> },
  { path: 'settings',                        section: 'settings',                         el: <SettingsManager /> },
  { path: 'email-management',                section: 'email_management',                 el: <EmailManagement /> },
  { path: 'mail',                            section: 'mail',                             el: <MailManager /> },
  { path: 'pages',                           section: 'pages',                            el: <PagesManager /> },
  { path: 'users',                           adminOnly: true,                             el: <UsersManager /> },
  { path: 'members',                         section: 'members',                          el: <MembersManager /> },
  { path: 'members/:memberId/signatures',    section: 'members',                          el: <MemberSignatures /> },
  { path: 'members/:memberId/logins',        section: 'members',                          el: <MemberLogins /> },
  { path: 'members/:memberId/points',        section: 'members',                          el: <MemberPoints /> },
  { path: 'rewards',                         section: 'rewards',                          el: <RewardsManager /> },
  { path: 'member-levels',                   section: 'member_levels',                    el: <MemberLevelsManager /> },
  { path: 'member-types',                    section: 'member_types',                     el: <MemberTypesManager /> },
  { path: 'membership-settings',             section: 'membership_settings',              el: <MembershipSettingsManager /> },
  { path: 'analytics',                       section: 'analytics',                        el: <AnalyticsDashboard /> },
  { path: 'seo',                             section: 'seo',                              el: <SeoManager /> },
  { path: 'backup',                          section: 'backup',                           el: <BackupManager /> },
  { path: 'section-order',                   section: 'section_order',                    el: <SectionOrderManager /> },
  { path: 'aurex-sections',                  section: 'aurex_sections',                   el: <AurexSectionsManager /> },
  { path: 'landing-content',                 section: 'landing_content',                  el: <LandingContentManager /> },
  { path: 'landing-hero',                    section: 'landing_hero',                     el: <LandingHeroManager /> },
  { path: 'landing-hero/add',                section: 'landing_hero',                     el: <LandingHeroSlideForm /> },
  { path: 'landing-hero/edit/:id',           section: 'landing_hero',                     el: <LandingHeroSlideForm /> },
  { path: 'landing-subscribers',             section: 'landing_subscribers',              el: <LandingSubscribersManager /> },
  { path: 'landing-contacts',                section: 'landing_contacts',                 el: <LandingContactsManager /> },
  { path: 'enrollment-fields',               section: 'enrollment_fields',                el: <EnrollmentFieldsManager /> },
  { path: 'documentation',                   section: ['doc_flow_diagram','doc_technical','doc_operator_manual','doc_user_guide','doc_testing_manual','doc_aws_install','doc_feature_audit'], el: <DocumentationManager /> },
  { path: 'quick-links',                     section: 'quick_links',                      el: <QuickLinksManager /> },
  { path: 'myaccount-nav',                   section: 'myaccount_nav',                    el: <MyAccountNavManager /> },
  { path: 'calendar/global',                 section: 'calendar_global',                  el: <GlobalEventsManager /> },
  { path: 'calendar/mentorship',             section: 'calendar_mentorship',              el: <MentorshipScheduleManager /> },
  { path: 'calendar/mentor-slot-templates',  section: 'calendar_mentor_slot_templates',   el: <MentorSlotTemplatesManager /> },
  { path: 'calendar/blocked-dates',          section: 'calendar_blocked_dates',           el: <BlockedDatesManager /> },
  { path: 'calendar/bundles',                section: 'calendar_bundles',                 el: <AdminBundlesManager /> },
  { path: 'calendar/coupons',                section: 'calendar_coupons',                 el: <AdminCouponsManager /> },
  { path: 'payouts',                         section: 'payouts',                          el: <AdminPayoutsManager /> },
  { path: 'geo',                             section: 'geo',                              el: <GeoManager /> },
  { path: 'roles',                           adminOnly: true,                             el: <RolesManager /> },
  { path: 'pros',                            adminOnly: true,                             el: <ProsManager /> },
];

function renderAdminRoutes() {
  return ADMIN_ROUTES.map(r => {
    const el = r.adminOnly
      ? <AdminOnlyRoute>{r.el}</AdminOnlyRoute>
      : (r.section ? <CmsSectionGuard section={r.section}>{r.el}</CmsSectionGuard> : r.el);
    return r.index
      ? <Route key="index" index element={el} />
      : <Route key={r.path} path={r.path} element={el} />;
  });
}

function AdminOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <CmsSectionGuard section="__admin_only__">{children}</CmsSectionGuard>;
  return children;
}

function PageProtectedRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { member, loading: memberLoading } = useMember();
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [checking, setChecking] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    publicAPI.getNavPages().then(r => {
      const pages = r.data || [];
      const path = location.pathname;
      const found = pages.find(p => p.url === path || `/page/${p.id}` === path);
      setPageData(found || null);
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [location.pathname]);

  if (checking || authLoading || memberLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full"></div></div>;

  // If page requires login. The entire Personal mini-site (category === 'personal')
  // is private by definition, so any Personal page is gated behind login even if
  // its individual "Login Required" flag was left off.
  if (pageData?.login_required || pageData?.category === 'personal') {
    // Admin bypasses all restrictions
    if (user?.role === 'admin') return children;

    // If member is logged in, check their type's allowed_pages
    if (member) {
      const allowedPages = member._member_type?.allowed_pages || [];
      const pageId = pageData.id;
      if (allowedPages.length > 0 && !allowedPages.includes(pageId)) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center max-w-md mx-auto px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary, #1a2332)' }}>
                <span className="text-white text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>L</span>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-heading, #1a2332)', fontFamily: 'Playfair Display, serif' }}>Access Restricted</h2>
              <p className="text-slate-500 text-sm mb-6">Your membership type does not include access to this page.</p>
            </div>
          </div>
        );
      }
      return children;
    }

    // Not logged in at all — show login prompt
    if (!user && !member) {
      return (
        <>
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center max-w-md mx-auto px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary, #1a2332)' }}>
                <span className="text-white text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>L</span>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-heading, #1a2332)', fontFamily: 'Playfair Display, serif' }}>Login Required</h2>
              <p className="text-slate-500 text-sm mb-6">You need to be logged in to access this page.</p>
              <button onClick={() => setShowLogin(true)} className="px-6 py-2.5 rounded-sm text-sm font-medium" style={{ backgroundColor: 'var(--color-button-bg, #1a2332)', color: 'var(--color-button-text, #fff)' }} data-testid="page-login-btn">
                Login to Continue
              </button>
            </div>
          </div>
          <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
        </>
      );
    }
  }

  return children;
}

// Personal Brand mini-site home ("personality" landing page). Lifestyle and
// Personal each reuse the HomePage section engine with a different personality.
// On any non-Personal-Brand theme these URLs fall back to the normal dynamic
// page resolver so a custom CMS page at the same path still works.
// `requireLogin` gates the entire Personal mini-site behind a logged-in session.
// Root landing page ("/"). Renders whichever mini-site the admin set as #1 in
// the personality order (defaults to Business). On non-PB themes the personality
// prop is ignored, so the normal homepage renders unchanged.
// Per-mini-site public/private gate. When a Personal Brand mini-site is set to
// "Members Only" (settings.pb_personality_visibility[key] === 'members'), guests
// are redirected to login; logged-in members (or admins) pass through. Public
// mini-sites (the default) render their children unchanged — so this is a no-op
// on every non-PB theme and for any personality left as 'public'.
function MiniSitePrivacyGate({ personality, children }) {
  const settings = useSettings();
  const { user, loading: authLoading } = useAuth();
  const { member, loading: memberLoading } = useMember();
  if (getPersonalityVisibility(settings, personality) !== 'members') return children;
  if (authLoading || memberLoading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full"></div></div>;
  }
  const loggedIn = (user && (user.id || user.member_id || user.username || user.email)) || member;
  // Guests are bounced to the public homepage ("/"), where the login modal lives
  // for users who do have credentials — not to a standalone login screen.
  // The homepage mini-site (#1) is itself that redirect target, so it is never
  // gated here (redirecting "/" → "/" would loop); it stays the public landing.
  if (!loggedIn && personality !== getRootPersonality(settings)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function RootMiniSite() {
  const settings = useSettings();
  const root = getRootPersonality(settings);
  return <MiniSitePrivacyGate personality={root}><HomePage personality={root} /></MiniSitePrivacyGate>;
}

function MiniSiteHome({ personality, requireLogin = false }) {
  const theme = useTheme();
  const settings = useSettings();
  const { user, loading: authLoading } = useAuth();
  const { member, loading: memberLoading } = useMember();
  const [showLogin, setShowLogin] = useState(false);

  if (theme !== 'personalbrand') {
    return <PageProtectedRoute><DynamicPage /></PageProtectedRoute>;
  }

  // Canonical URL: the #1 mini-site is served at "/", so visiting its own slug
  // (e.g. /lifestyle when Lifestyle is #1) redirects to "/" — one page, one URL.
  if (personality === getRootPersonality(settings)) {
    return <Navigate to="/" replace />;
  }

  if (requireLogin) {
    if (authLoading || memberLoading) {
      return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full"></div></div>;
    }
    const loggedIn = (user && (user.id || user.member_id || user.username || user.email)) || member;
    if (!loggedIn) {
      return (
        <>
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center max-w-md mx-auto px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary, #1a2332)' }}>
                <span className="text-white text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>L</span>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-heading, #1a2332)', fontFamily: 'Playfair Display, serif' }}>Private Area</h2>
              <p className="text-slate-500 text-sm mb-6">The Personal space is reserved for the inner circle. Please log in to continue.</p>
              <button onClick={() => setShowLogin(true)} className="px-6 py-2.5 rounded-sm text-sm font-medium" style={{ backgroundColor: 'var(--color-button-bg, #1a2332)', color: 'var(--color-button-text, #fff)' }} data-testid="personal-login-btn">
                Login to Continue
              </button>
            </div>
          </div>
          <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
        </>
      );
    }
  }

  // Per-mini-site public/members gate (independent of the inner-circle requireLogin above).
  return <MiniSitePrivacyGate personality={personality}><HomePage personality={personality} /></MiniSitePrivacyGate>;
}

function MemberRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0d0f14]"><div className="animate-spin w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/my-account/login" replace />;
  // My Account access gate — admins always pass; everyone else must hold
  // role_member.  If an admin revokes role_member from this user, the very
  // next render bounces them back to the login screen.
  const allowedAccess = user.role === 'admin' || (user.cms_roles || []).includes('role_member');
  if (!allowedAccess) return <Navigate to="/my-account/login" replace />;
  return children;
}

// Maps system page URLs to their hero page identifiers
const SYSTEM_PAGE_MAP = {
  '/news': 'news',
  '/gallery': 'gallery',
  '/reading-list': 'reading-list',
};

function SystemPageHero() {
  const location = useLocation();
  const [heroSlides, setHeroSlides] = useState([]);
  const pageId = SYSTEM_PAGE_MAP[location.pathname];

  useEffect(() => {
    if (!pageId) { setHeroSlides([]); return; }
    publicAPI.getHeroSlides(pageId).then(r => setHeroSlides(r.data || [])).catch(() => setHeroSlides([]));
  }, [pageId]);

  if (heroSlides.length === 0) return null;

  // Lazy-load HeroSection to avoid circular import issues
  const HeroSection = require('./components/HeroSection').default;
  return <HeroSection slides={heroSlides} />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.substring(1);
      const tryScroll = (attempts) => {
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
        else if (attempts > 0) { setTimeout(() => tryScroll(attempts - 1), 200); }
      };
      setTimeout(() => tryScroll(5), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function useLandingActive(settings) {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!settings.landing_page_enabled) { setActive(false); return; }
    const check = () => {
      if (!settings.landing_page_launch_date) { setActive(true); return; }
      const launch = new Date(settings.landing_page_launch_date).getTime();
      setActive(Date.now() < launch);
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [settings.landing_page_enabled, settings.landing_page_launch_date]);
  return active;
}

// Fallback shown while a lazy-loaded admin/member chunk is fetched.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin w-8 h-8 border-2 border-[var(--color-accent,#0D9488)] border-t-transparent rounded-full" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const settings = useSettings();
  const landingActive = useLandingActive(settings);

  // Show nothing until settings are loaded (prevents flash of main site before landing page)
  if (!settings._loaded) {
    return <div className="min-h-screen" style={{ backgroundColor: '#0a0a12' }} />;
  }

  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  const isMemberArea = location.pathname.startsWith('/my-account');
  const isAdmin = location.pathname.startsWith('/admin');

  if (isMemberArea) {
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/my-account/login" element={<MemberLogin />} />
        <Route path="/my-account/register" element={<MemberRegister />} />
        <Route path="/my-account/forgot-password" element={<MemberForgotPassword />} />
        <Route path="/my-account/reset-password" element={<MemberResetPassword />} />
        <Route path="/my-account" element={<MemberRoute><MyAccountLayout /></MemberRoute>}>
          <Route index element={<Navigate to="/my-account/membership-profile" replace />} />
          <Route path="membership-profile" element={<MembershipProfile />} />
          <Route path="mentorship-profile" element={<MentorshipProfile />} />
          <Route path="my-sponsor" element={<MySponsor />} />
          <Route path="invite-code" element={<InviteCode />} />
          <Route path="ebank" element={<MyEbank />} />
          <Route path="my-community" element={<MyCommunity />} />
          <Route path="portfolios" element={<PortfolioList />} />
          <Route path="portfolios/new" element={<PortfolioForm />} />
          <Route path="portfolios/:id" element={<PortfolioDetail />} />
          <Route path="portfolios/:id/edit" element={<PortfolioForm />} />
          <Route path="global-calendar" element={<GlobalCalendar />} />
          <Route path="event/:eventId" element={<EventDetail />} />
          <Route path="mentorship-calendar" element={<MentorshipCalendar />} />
          <Route path="mentor-calendar" element={<MentorCalendarView />} />
          <Route path="my-bookings" element={<MyBookings />} />
          <Route path="calendar-sync" element={<CalendarSync />} />
          <Route path="mail" element={<MemberMail />} />
          <Route path="mentorship/checkout-success" element={<MentorshipCheckoutSuccess />} />
          <Route path="earnings" element={<MentorEarnings />} />
          <Route path="points" element={<PointsRewards />} />
          <Route path="bundles" element={<BundlesBrowse />} />
          <Route path="bundles/checkout-success" element={<BundleCheckoutSuccess />} />
          <Route path="bundles/:id" element={<BundleDetail />} />
        </Route>
      </Routes>
      </Suspense>
    );
  }

  // Landing page takes over all non-admin, non-my-account routes
  if (landingActive && !isAdmin) {
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          {renderAdminRoutes()}
        </Route>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/membership-enrollment" element={<MembershipEnrollment />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/membership-enrollment" element={<MembershipEnrollment />} />
        <Route path="*" element={
          <>
            <Navbar />
            <SystemPageHero />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<RootMiniSite />} />
        <Route path="/business" element={<MiniSiteHome personality="business" />} />
        <Route path="/lifestyle" element={<MiniSiteHome personality="lifestyle" />} />
        <Route path="/personal" element={<MiniSiteHome personality="personal" />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:slug" element={<NewsDetailPage />} />
        <Route path="/reading-list" element={<ReadingListPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/map/:slug" element={<MapDetailPage />} />
        <Route path="/service/:serviceId" element={<ServiceDetailPage />} />
        <Route path="/featured-projects" element={<FeaturedProjectsPage />} />
        <Route path="/conferences" element={<ConferencesPage />} />
        <Route path="/recommended_sites" element={<RecommendedSitesPage />} />
        <Route path="/album/:albumId" element={<LayoutSubGallery />} />
        <Route path="/terms" element={<PageProtectedRoute><DynamicPage /></PageProtectedRoute>} />
        <Route path="/privacy" element={<PageProtectedRoute><DynamicPage /></PageProtectedRoute>} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/page/:pageId" element={<PageProtectedRoute><DynamicPage /></PageProtectedRoute>} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          {renderAdminRoutes()}
        </Route>
        <Route path="/membership-enrollment" element={<MembershipEnrollment />} />
        {/* Catch-all: custom page URLs like /kls */}
        <Route path="*" element={<PageProtectedRoute><DynamicPage /></PageProtectedRoute>} />
      </Routes>
      </Suspense>
      <Footer />
      <BackToTop />
    </>
    } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <MemberProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </MemberProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
